<template>
  <div
    v-show="preview"
    class="relative"
  >
    <div
      ref="canvasElement"
      class="aspect-video bg-black"
    />
    <video
      ref="cameraVideoElement"
      class="video-layout-offscreen"
      muted
      autoplay
    />
    <video
      ref="screenVideoElement"
      class="video-layout-offscreen"
      muted
      autoplay
    />
    <canvas
      ref="canvasElement2"
      class="aspect-video w-full bg-black hidden"
    />
  </div>
</template>

<script>
import {mapState} from 'vuex'
import Konva from 'konva'

export default {
  name: 'VideoLayout',
  props: {
    responsive: {
      type: Boolean,
      default: false
    },
    live: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      worker: null,
      layer: null,
      stage: null,
      cameraImage: null,
      screenImage: null,
      cameraTransformer: null,
      screenTransformer: null,
      canvasStream: null,
      cameraTrack: null,
      imageCapture: null,
      screenStream: null
    }
  },
  computed: {
    ...mapState('liveStream', {
      selectedAudioDevice: state => state.selectedAudioDevice,
      selectedVideoDevice: state => state.selectedVideoDevice,
      layout: state => state.layout,
      preview: state => state.preview
    }),
    deviceSelection () {
      return [this.selectedVideoDevice, this.selectedAudioDevice]
    }
  },
  methods: {
    requestAnimationFrame (cb) {
      setTimeout(cb, 32)
    },
    updateCameraImageSize () {
      const rectWidth = this.cameraImage.width()
      const rectHeight = this.cameraImage.height()

      const videoAspectRatio = this.$refs.cameraVideoElement.videoWidth / this.$refs.cameraVideoElement.videoHeight
      const rectAspectRatio = rectWidth / rectHeight

      let newWidth, newHeight
      if (rectAspectRatio > videoAspectRatio) {
        newWidth = rectWidth
        newHeight = rectWidth / videoAspectRatio
      } else {
        newWidth = rectHeight * videoAspectRatio
        newHeight = rectHeight
      }

      this.cameraImage.crop({
        x: (newWidth - rectWidth) / 2 / newWidth * this.$refs.cameraVideoElement.videoWidth,
        y: (newHeight - rectHeight) / 2 / newHeight * this.$refs.cameraVideoElement.videoHeight,
        width: this.$refs.cameraVideoElement.videoWidth * (rectWidth / newWidth),
        height: this.$refs.cameraVideoElement.videoHeight * (rectHeight / newHeight)
      })

      this.cameraImage.getLayer().batchDraw()
    },
    updateScreenImageSize () {
      const rectWidth = this.screenImage.width()
      const rectHeight = this.screenImage.height()

      const videoWidth = this.$refs.screenVideoElement.videoWidth
      const videoHeight = this.$refs.screenVideoElement.videoHeight

      const videoAspectRatio = videoWidth / videoHeight
      const rectAspectRatio = rectWidth / rectHeight

      let newWidth, newHeight

      if (rectAspectRatio > videoAspectRatio) {
        // Если контейнер шире, чем видео, подгоняем по высоте
        newHeight = rectHeight
        newWidth = newHeight * videoAspectRatio
      } else {
        // Если контейнер уже, подгоняем по ширине
        newWidth = rectWidth
        newHeight = newWidth / videoAspectRatio
      }

      // Устанавливаем размеры для screenImage, чтобы оно соответствовало контейнеру
      this.screenImage.width(newWidth)
      this.screenImage.height(newHeight)

      // Центрируем изображение внутри контейнера
      this.screenImage.x((rectWidth - newWidth) / 2)
      this.screenImage.y((rectHeight - newHeight) / 2)

      this.screenImage.getLayer().batchDraw()
    },
    sendTransformDataToWorker (name, data) {
      if (typeof this.worker !== 'undefined') {
        this.worker.postMessage({
          [name]: data
        })
      }
    },
    initStage () {
      this.stage = new Konva.Stage({
        container: this.$refs.canvasElement,
        width: this.$refs.canvasElement.clientWidth,
        height: this.$refs.canvasElement.clientHeight
      })

      this.layer = new Konva.Layer()
      this.stage.add(this.layer)

      this.cameraScene()
    },
    cameraScene () {
      navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: this.selectedVideoDevice,
          height: {ideal: 1080, max: 2160, min: 720},
          width: {ideal: 1920, max: 3840, min: 1280}
        },
        audio: {
          deviceId: this.selectedAudioDevice
        }
      }).then((stream) => {
        this.$refs.cameraVideoElement.srcObject = stream
        if (this.cameraTrack) {
          this.cameraTrack.stop()
        }

        const audioTracks = stream.getAudioTracks()

        if (this.cameraImage) {
          this.cameraTrack = stream.getVideoTracks()[0]
          this.cameraImage.image(this.$refs.cameraVideoElement)

          if (this.canvasStream) {
            this.canvasStream.getAudioTracks().forEach(track => {
              this.canvasStream.removeTrack(track)
            })

            audioTracks.forEach(track => this.canvasStream.addTrack(track))
          }

          this.imageCapture = new ImageCapture(this.cameraTrack)

          if (this.worker) {
            const captureFrame = () => {
              if (this.cameraTrack) {
                this.imageCapture.grabFrame().then((bitmap) => {
                  this.worker.postMessage({videoFrame: bitmap}, [bitmap])
                  captureFrame()
                }).catch(error => {
                  console.error('Error capturing frame:', error)
                })
              }
            }

            captureFrame()
          }
        } else {
          this.cameraTrack = stream.getVideoTracks()[0]
          this.cameraImage = new Konva.Image({
            x: 0,
            y: 0,
            image: this.$refs.cameraVideoElement,
            width: this.$refs.canvasElement.clientWidth,
            height: this.$refs.canvasElement.clientHeight,
            draggable: true,
            zIndex: 10
          })

          this.cameraImage.on('transform dragmove', () => {
            const position = this.cameraImage.getClientRect()
            this.sendTransformDataToWorker('cameraImageTransform', {
              x: position.x,
              y: position.y,
              cornerRadius: this.cameraImage.cornerRadius(),
              width: position.width,
              height: position.height,
              scaleX: this.cameraImage.scaleX(),
              scaleY: this.cameraImage.scaleY(),
              crop: this.cameraImage.crop()
            })
          })

          this.layer.add(this.cameraImage)

          // Добавляем возможность изменения размера через трансформер
          this.cameraTransformer = new Konva.Transformer({
            node: this.cameraImage,
            rotateEnabled: false,
            enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
            boundBoxFunc: (oldBox, newBox) => {
              // Ограничение на минимальный размер
              if (newBox.width < 30 || newBox.height < 30) {
                return oldBox
              }
              return newBox
            }
          })

          this.layer.add(this.cameraTransformer)
          this.cameraTransformer.nodes([])

          this.cameraImage.on('transform', this.updateCameraImageSize)
          this.cameraImage.on('transformend', this.updateCameraImageSize)

          this.stage.on('click', (e) => {
            // Если клик был не на элементе screenImage, скрываем трансформер
            if (e.target !== this.cameraImage) {
              this.cameraTransformer.nodes([])
            } else {
              this.cameraTransformer.nodes([this.cameraImage])
            }
            this.layer.draw()
          })

          const offscreenCanvas = this.$refs.canvasElement2.transferControlToOffscreen()
          this.worker = new Worker('/workers/canvasWorker.js')
          this.worker.postMessage({canvas: offscreenCanvas, width: this.responsive ? this.$refs.canvasElement.clientWidth : false, height: this.$refs.canvasElement.clientHeight}, [offscreenCanvas])

          this.imageCapture = new ImageCapture(this.cameraTrack)

          const captureFrame = () => {
            if (this.cameraTrack) {
              this.imageCapture.grabFrame().then((bitmap) => {
                this.worker.postMessage({videoFrame: bitmap}, [bitmap])
                captureFrame()
              }).catch(error => {
                console.error(!error)
                console.error('1Error capturing frame:', error)
              })
            }
          }

          captureFrame()

          setTimeout(() => {
            this.canvasStream = this.$refs.canvasElement2.captureStream(30)
            audioTracks.forEach(track => this.canvasStream.addTrack(track))
            this.$store.commit('liveStream/SET_STREAM', this.canvasStream)
          }, 100)

          const updateCameraFrame = () => {
            if (this.cameraImage.getLayer()) {
              this.cameraImage.getLayer().batchDraw()
              requestAnimationFrame(updateCameraFrame)
            }
          }

          updateCameraFrame()

          setTimeout(() => {
            this.updateCameraImageSize()
          }, 1000)

          this.cameraImage.moveToTop()
        }
      })
    },
    screenShare (screen) {
      return new Promise((resolve, reject) => {
        if (screen) {
          this.$refs.screenVideoElement.srcObject = screen
          resolve()
          return
        }

        navigator.mediaDevices.getDisplayMedia({
          video: {
            height: { ideal: 1080, max: 1080 },
            width: { ideal: 1920, max: 1920 },
            displaySurface: 'monitor'
          }
        }).then(async (stream) => {
          if (this.screenImage) {
            await this.removeScreenShare()
          }

          this.$refs.screenVideoElement.srcObject = stream
          this.screenStream = stream
          this.screenImage = new Konva.Image({
            x: 0,
            y: 0,
            image: this.$refs.screenVideoElement,
            width: this.$refs.canvasElement.clientWidth,
            height: this.$refs.canvasElement.clientHeight,
            draggable: true,
            zIndex: 1
          })

          this.layer.add(this.screenImage)
          this.screenImage.moveToBottom()

          this.screenImage.on('transform dragmove', () => {
            const position = this.screenImage.getClientRect()
            this.sendTransformDataToWorker('screenImageTransform', {
              x: position.x,
              y: position.y,
              cornerRadius: this.screenImage.cornerRadius(),
              width: position.width,
              height: position.height,
              scaleX: this.screenImage.scaleX(),
              scaleY: this.screenImage.scaleY(),
              crop: this.screenImage.crop()
            })
          })

          // Добавляем возможность изменения размера через трансформер
          this.screenTransformer = new Konva.Transformer({
            node: this.screenImage,
            rotateEnabled: false,
            enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
            boundBoxFunc: (oldBox, newBox) => {
              // Ограничение на минимальный размер
              if (newBox.width < 50 || newBox.height < 50) {
                return oldBox
              }
              return newBox
            }
          })
          this.layer.add(this.screenTransformer)
          this.screenTransformer.nodes([])

          this.stage.on('click', (e) => {
            // Если клик был не на элементе screenImage, скрываем трансформер
            if (e.target !== this.screenImage) {
              this.screenTransformer.nodes([])
            } else {
              this.screenTransformer.nodes([this.screenImage])
            }

            this.layer.draw()
          })

          //
          const track = stream.getVideoTracks()[0]
          const imageCapture = new ImageCapture(track)

          const captureFrame = () => {
            imageCapture.grabFrame().then((bitmap) => {
              this.worker.postMessage({videoFrame2: bitmap}, [bitmap])
              captureFrame()
            }).catch(error => {
              console.error(!error)
              console.error('wError capturing frame:', error)
            })
          }

          captureFrame()

          track.onended = () => {
            this.selectLayout('layout1')
            this.screenStream = null
          }

          // Обновляем холст при каждом кадре
          const updateScreenFrame = () => {
            if (this.screenImage.getLayer()) {
              this.screenImage.getLayer().batchDraw()
              requestAnimationFrame(updateScreenFrame)
            }
          }

          updateScreenFrame()
          setTimeout(() => {
            this.updateScreenImageSize()
            this.worker.postMessage({startScreen: true})
            resolve()
          }, 100)
        })
          .catch((err) => {
            console.error('Error starting screen share:', err)
            reject(err)
          })
      })
    },
    resizeStage () {
      if (this.stage) {
        this.stage.width(this.$refs.canvasElement.clientWidth)
        this.stage.height(this.$refs.canvasElement.clientHeight)
      }
    },
    removeScreenShare () {
      return new Promise((resolve) => {
        this.screenStream = null

        if (this.screenImage) {
          this.screenImage.destroy()
          this.screenTransformer.nodes([])
          this.layer.draw()

          const stream = this.$refs.screenVideoElement.srcObject

          if (stream) {
            stream.getVideoTracks().forEach(track => track.stop())
            this.$refs.screenVideoElement.srcObject = null
          }

          this.worker.postMessage({stopScreen: true})
        }

        resolve()
      })
    },
    removeCamera () {
      if (this.cameraImage) {
        this.cameraTransformer.nodes([])
        this.cameraImage.visible(false)
      }
    },
    selectLayout (layout) {
      const layouts = {
        layout1: () => {
          this.removeScreenShare()
          new Konva.Tween({
            node: this.cameraImage,
            scaleY: 1,
            scaleX: 1,
            duration: .2, // Длительность анимации в секундах
            cornerRadius: 0,
            y: 0,
            x: 0,
            visible: true,
            width: this.$refs.canvasElement.clientWidth,
            height: this.$refs.canvasElement.clientHeight,
            onUpdate: () => {
              this.updateCameraImageSize()
            },
            onFinish: () => {
              const position = this.cameraImage.getClientRect()
              this.sendTransformDataToWorker('transitionCameraLayout', {
                x: position.x,
                y: position.y,
                cornerRadius: this.cameraImage.cornerRadius(),
                width: position.width,
                height: position.height,
                visible: position.visible
              })
            }
          }).play()
        },
        layout2: () => {
          this.removeScreenShare()
          new Konva.Tween({
            node: this.cameraImage,
            scaleY: 1,
            scaleX: 1,
            duration: .2, // Длительность анимации в секундах
            cornerRadius: 10,
            y: 30,
            x: 30,
            visible: true,
            width: this.$refs.canvasElement.clientWidth - 60,
            height: this.$refs.canvasElement.clientHeight - 60,
            onUpdate: () => {
              this.updateCameraImageSize()
            },
            onFinish: () => {
              const position = this.cameraImage.getClientRect()
              this.sendTransformDataToWorker('transitionCameraLayout', {
                x: position.x,
                y: position.y,
                cornerRadius: this.cameraImage.cornerRadius(),
                width: position.width,
                height: position.height,
                visible: position.visible
              })
            }
          }).play()
        },
        layout3: () => {
          this.screenShare(this.screenStream)
            .then(() => {
              new Konva.Tween({
                node: this.cameraImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 100,
                y: this.stage.height() - 150,
                x: this.stage.width() - 150,
                visible: true,
                width: 150,
                height: 150,
                onUpdate: () => {
                  this.updateCameraImageSize()
                },
                onFinish: () => {
                  const camPosition = this.cameraImage.getClientRect()
                  this.sendTransformDataToWorker('transitionCameraLayout', {
                    x: camPosition.x,
                    y: camPosition.y,
                    cornerRadius: this.cameraImage.cornerRadius(),
                    width: camPosition.width,
                    height: camPosition.height,
                    visible: true
                  })

                  const screenPosition = this.screenImage.getClientRect()
                  this.sendTransformDataToWorker('transitionScreenLayout', {
                    y: (this.stage.height() - this.screenImage.height()) / 2,
                    x: (this.stage.width() - this.screenImage.width()) / 2,
                    cornerRadius: this.screenImage.cornerRadius(),
                    width: screenPosition.width,
                    height: screenPosition.height,
                    visible: screenPosition.visible
                  })
                }
              }).play()
            })
        },
        layout4: () => {
          this.screenShare(this.screenStream)
            .then(() => {
              new Konva.Tween({
                node: this.cameraImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 10,
                y: this.stage.height() - 150,
                x: this.stage.width() - 150,
                visible: true,
                width: 150,
                height: 150,
                onUpdate: () => {
                  this.updateCameraImageSize()
                },
                onFinish: () => {
                  const camPosition = this.cameraImage.getClientRect()
                  this.sendTransformDataToWorker('transitionCameraLayout', {
                    x: camPosition.x,
                    y: camPosition.y,
                    cornerRadius: this.cameraImage.cornerRadius(),
                    width: camPosition.width,
                    height: camPosition.height,
                    visible: true
                  })

                  const screenPosition = this.screenImage.getClientRect()
                  this.sendTransformDataToWorker('transitionScreenLayout', {
                    y: (this.stage.height() - this.screenImage.height()) / 2,
                    x: (this.stage.width() - this.screenImage.width()) / 2,
                    cornerRadius: this.screenImage.cornerRadius(),
                    width: screenPosition.width,
                    height: screenPosition.height,
                    visible: screenPosition.visible
                  })
                }
              }).play()
            })
        },
        layout5: () => {
          this.screenShare(this.screenStream)
            .then(() => {
              new Konva.Tween({
                node: this.screenImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 10,
                y: (this.stage.height() - this.screenImage.height()) / 2,
                x: (this.stage.width() - this.screenImage.width()) / 2,
                visible: true,
                width: this.$refs.canvasElement.clientWidth - 300,
                onUpdate: () => {
                  this.updateScreenImageSize()
                  this.screenImage.y((this.stage.height() - this.screenImage.height()) / 2)
                  this.screenImage.x((this.stage.width() - this.screenImage.width()) / 2)
                },
                onFinish: () => {
                  new Konva.Tween({
                    node: this.cameraImage,
                    scaleY: 1,
                    scaleX: 1,
                    duration: .2, // Длительность анимации в секундах
                    cornerRadius: 10,
                    y: (this.screenImage.height() + ((this.stage.height() - this.screenImage.height()) / 2)) - 135,
                    x: (this.stage.width() - this.$refs.canvasElement.clientWidth - 300) + this.$refs.canvasElement.clientWidth + 25,
                    visible: true,
                    width: 150,
                    height: 150,
                    onUpdate: () => {
                      this.updateCameraImageSize()
                    },
                    onFinish: () => {
                      const camPosition = this.cameraImage.getClientRect()
                      this.sendTransformDataToWorker('transitionCameraLayout', {
                        x: camPosition.x,
                        y: camPosition.y,
                        cornerRadius: this.cameraImage.cornerRadius(),
                        width: camPosition.width,
                        height: camPosition.height,
                        visible: true
                      })

                      const screenPosition = this.screenImage.getClientRect()
                      this.sendTransformDataToWorker('transitionScreenLayout', {
                        y: (this.stage.height() - this.screenImage.height()) / 2,
                        x: (this.stage.width() - this.screenImage.width()) / 2,
                        cornerRadius: this.screenImage.cornerRadius(),
                        width: screenPosition.width,
                        height: screenPosition.height,
                        visible: screenPosition.visible
                      })
                    }
                  }).play()
                }
              }).play()
            })
        },
        layout6: () => {
          this.screenShare(this.screenStream)
            .then(() => {
              new Konva.Tween({
                node: this.screenImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 10,
                y: (this.stage.height() - this.screenImage.height()) / 2,
                x: (this.stage.width() - this.screenImage.width()) / 2,
                visible: true,
                width: this.$refs.canvasElement.clientWidth - 300,
                onUpdate: () => {
                  this.updateScreenImageSize()
                  this.screenImage.y((this.stage.height() - this.screenImage.height()) / 2)
                  this.screenImage.x((this.stage.width() - this.screenImage.width()) / 2)
                },
                onFinish: () => {
                  new Konva.Tween({
                    node: this.cameraImage,
                    scaleY: 1,
                    scaleX: 1,
                    duration: .2, // Длительность анимации в секундах
                    cornerRadius: 100,
                    y: (this.screenImage.height() + ((this.stage.height() - this.screenImage.height()) / 2)) - 135,
                    x: (this.stage.width() - this.$refs.canvasElement.clientWidth - 300) + this.$refs.canvasElement.clientWidth + 25,
                    visible: true,
                    width: 150,
                    height: 150,
                    onUpdate: () => {
                      this.updateCameraImageSize()
                    },
                    onFinish: () => {
                      const camPosition = this.cameraImage.getClientRect()
                      this.sendTransformDataToWorker('transitionCameraLayout', {
                        x: camPosition.x,
                        y: camPosition.y,
                        cornerRadius: this.cameraImage.cornerRadius(),
                        width: camPosition.width,
                        height: camPosition.height,
                        visible: true
                      })

                      const screenPosition = this.screenImage.getClientRect()
                      this.sendTransformDataToWorker('transitionScreenLayout', {
                        y: (this.stage.height() - this.screenImage.height()) / 2,
                        x: (this.stage.width() - this.screenImage.width()) / 2,
                        cornerRadius: this.screenImage.cornerRadius(),
                        width: screenPosition.width,
                        height: screenPosition.height,
                        visible: screenPosition.visible
                      })
                    }
                  }).play()
                }
              }).play()
            })
        },
        layout7: () => {
          this.screenShare()
            .then(() => {
              this.removeCamera()
              new Konva.Tween({
                node: this.screenImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 10,
                y: (this.stage.height() - this.screenImage.height()) / 2,
                x: (this.stage.width() - this.screenImage.width()) / 2,
                visible: true,
                width: this.$refs.canvasElement.clientWidth - 300,
                onUpdate: () => {
                  this.updateScreenImageSize()
                  this.screenImage.y((this.stage.height() - this.screenImage.height()) / 2)
                  this.screenImage.x((this.stage.width() - this.screenImage.width()) / 2)
                },
                onFinish: () => {
                  this.sendTransformDataToWorker('transitionCameraLayout', {
                    visible: false
                  })

                  const screenPosition = this.screenImage.getClientRect()
                  this.sendTransformDataToWorker('transitionScreenLayout', {
                    y: (this.stage.height() - this.screenImage.height()) / 2,
                    x: (this.stage.width() - this.screenImage.width()) / 2,
                    cornerRadius: this.screenImage.cornerRadius(),
                    width: screenPosition.width,
                    height: screenPosition.height,
                    visible: screenPosition.visible
                  })
                }
              }).play()
            })
        },
        layout8: () => {
          this.screenShare()
            .then(() => {
              this.removeCamera()
              new Konva.Tween({
                node: this.screenImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 0,
                visible: true,
                height: this.$refs.canvasElement.clientHeight,
                onUpdate: () => {
                  this.updateScreenImageSize()
                  this.screenImage.x((this.stage.width() - this.screenImage.width()) / 2)
                },
                onFinish: () => {
                  this.sendTransformDataToWorker('transitionCameraLayout', {
                    visible: false
                  })

                  const screenPosition = this.screenImage.getClientRect()
                  this.sendTransformDataToWorker('transitionScreenLayout', {
                    y: (this.stage.height() - this.screenImage.height()) / 2,
                    x: (this.stage.width() - this.screenImage.width()) / 2,
                    cornerRadius: this.screenImage.cornerRadius(),
                    width: screenPosition.width,
                    height: screenPosition.height,
                    visible: screenPosition.visible
                  })
                }
              }).play()
            })
            .catch((error) => {
              console.error('Failed to start screen share for layout8:', error)
            })
        },
        layout9: () => {
          this.screenShare()
            .then(() => {

              new Konva.Tween({
                node: this.cameraImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 10,
                y: 10,
                x: this.stage.width() - 160,
                visible: true,
                width: 150,
                height: 150,
                onUpdate: () => {
                  this.updateCameraImageSize()
                }
              }).play()

              new Konva.Tween({
                node: this.screenImage,
                scaleY: 1,
                scaleX: 1,
                duration: .2, // Длительность анимации в секундах
                cornerRadius: 0,
                y: (this.stage.height() - this.screenImage.height()) / 2,
                x: 0,
                visible: true,
                width: this.$refs.canvasElement.clientWidth - 170,
                onUpdate: () => {
                  this.updateScreenImageSize()
                  this.screenImage.y((this.stage.height() - this.screenImage.height()) / 2)
                }
              }).play()
            })
        }
      }

      layouts[layout]()
    }
  },
  watch: {
    deviceSelection: {
      handler () {
        this.cameraScene()
      },
      deep: true
    },
    layout (val) {
      if (val) {
        this.selectLayout(val)
      }
    }
  },
  mounted () {
    this.initStage()
    window.addEventListener('resize', this.resizeStage)
  },
  beforeDestroy () {
    // Очищаем обработчик событий при размонтировании компонента
    window.removeEventListener('resize', this.resizeStage)
  }
}
</script>

<style scoped>
.video-layout-offscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  z-index: -9999;
}
</style>
